import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import LanguageIcon from '@mui/icons-material/Language'
import SchoolIcon from '@mui/icons-material/School'
import StarsIcon from '@mui/icons-material/Stars'
import TimelapseIcon from '@mui/icons-material/Timelapse'
import WorkIcon from '@mui/icons-material/Work'
import Grid from '@mui/material/Grid2'
import { LanguagesData } from 'api/UtilsAPI'
import LanguageChip from 'Components/reusable/CaleoCustomComponents/LanguageChip/LanguageChip'
import { differenceInCalendarDays } from 'date-fns'
import { useUser } from 'hooks'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ICvData } from 'types/profileInterfaces'

/** @notExported */
interface IOverviewItem {
  /** CV data. */
  data: ICvData | undefined
  /** Language options. */
  languages: LanguagesData
  /** Profile public ID. */
  publicId?: string
}

/**
 * Component for displaying overview of the profile qualifications.
 *
 * @returns Overview component.
 * @notExported
 */
const ProfileOverview: React.FC<IOverviewItem> = ({ data, languages, publicId }) => {
  const { t } = useTranslation()
  const { features } = useUser()

  const employerDays = useMemo(() => {
    let days = 0
    if (data) {
      for (const employer of data.Employers) {
        const endDate = employer.endDate ? new Date(employer.endDate) : new Date()
        days += differenceInCalendarDays(endDate, new Date(employer.startDate))
      }
    }
    return days
  }, [data])
  const projectDays = useMemo(() => {
    let days = 0
    if (data) {
      for (const project of data.Projects) {
        const endDate = project.endDate ? new Date(project.endDate) : new Date()
        days += differenceInCalendarDays(endDate, new Date(project.startDate))
      }
    }
    return days
  }, [data])
  const degreesCount = useMemo(() => {
    let count = 0
    if (data) {
      count = data.Education.length
    }
    return count
  }, [data])
  const coursesCount = useMemo(() => {
    let count = 0
    if (data) {
      count = data.Courses.length
    }
    return count
  }, [data])
  const certificatesCount = useMemo(() => {
    let count = 0
    if (data) {
      count = data.Certificates.length
    }
    return count
  }, [data])
  const competencesCount = useMemo(() => {
    if (features.includes('competences')) {
      let count = 0
      if (data) {
        count = data.Competences.length
      }
      return count
    }
    return 0
  }, [data, features])
  const languageChips = useMemo(() => {
    const items: React.ReactNode[] = []
    if (data) {
      for (const language of data.LanguageSkills) {
        items.push(<LanguageChip key={language.id} size="small" language={language} languages={languages} hideLevel />)
      }
    }
    return items
  }, [data, languages])
  const employerYearsAndMonths = useMemo(() => {
    let employerYears = Math.floor(employerDays / 365)
    let employerMonths = Math.round((employerDays % 365) / 30)
    if (employerMonths > 11) {
      employerMonths--
      employerYears++
    }
    return { years: employerYears, months: employerMonths }
  }, [employerDays])
  const projectYearsAndMonths = useMemo(() => {
    let projectYears = Math.floor(projectDays / 365)
    let projectMonths = Math.round((projectDays % 365) / 30)
    if (projectMonths > 11) {
      projectMonths = 0
      projectYears++
    }
    return { years: projectYears, months: projectMonths }
  }, [projectDays])

  const overviewItem = (
    icon: React.ReactElement,
    title: string,
    data: number | React.ReactNode[],
    suffix?: string,
    extra?: number,
    extraSuffix?: string
  ) => {
    const hasData = typeof data === 'number' ? data > 0 : data.length > 0
    if ((publicId && hasData) || !publicId) {
      return (
        <Grid container justifyContent="space-between">
          <Grid
            sx={{ whiteSpace: 'nowrap', justifyContent: 'flex-start', display: 'flex' }}
            size={{
              xs: 5,
              md: 4,
              lg: 5,
            }}
          >
            {icon} {t(title)}
          </Grid>
          <Grid
            size={{
              xs: 7,
              md: 8,
              lg: 7,
            }}
          >
            <Grid container direction="row-reverse" justifyContent="flex-start" spacing={1}>
              {extra && (
                <>
                  {extraSuffix && (
                    <Grid>
                      <span style={{ fontWeight: 'normal' }}>{t(extraSuffix)}</span>
                    </Grid>
                  )}
                  <Grid data-testid={`${title.split('.')[0]}-extra-suffix`}>{extra}</Grid>
                </>
              )}
              {suffix && (
                <Grid>
                  <span style={{ fontWeight: 'normal' }}>{t(suffix)}</span>
                </Grid>
              )}
              <Grid data-testid={`${title.split('.')[0]}-suffix`}>{data}</Grid>
            </Grid>
          </Grid>
        </Grid>
      )
    }
  }

  return (
    <Grid
      size={{
        md: 5,
      }}
    >
      <Grid container justifyContent="flex-end">
        <Grid sx={{ marginBottom: '10px', width: '100%', fontSize: 20, fontWeight: 'bold' }}>
          {overviewItem(
            <WorkIcon sx={{ mr: '5px' }} />,
            'employer.title',
            employerYearsAndMonths.years,
            'profile.skill.years.abbr',
            employerYearsAndMonths.months > 0 ? employerYearsAndMonths.months : undefined,
            employerYearsAndMonths.months > 0 ? 'profile.skill.months.abbr' : undefined
          )}
          {overviewItem(
            <TimelapseIcon sx={{ mr: '5px' }} />,
            'project.title',
            projectYearsAndMonths.years,
            'profile.skill.years.abbr',
            projectYearsAndMonths.months > 0 ? projectYearsAndMonths.months : undefined,
            projectYearsAndMonths.months > 0 ? 'profile.skill.months.abbr' : undefined
          )}
          {overviewItem(<SchoolIcon sx={{ mr: '5px' }} />, 'education.title', degreesCount, 'degrees')}
          {overviewItem(<StarsIcon sx={{ mr: '5px' }} />, 'courses.title', coursesCount, 'courses')}
          {overviewItem(
            <EmojiEventsIcon sx={{ mr: '5px' }} />,
            'certificates.title',
            certificatesCount,
            'certificates'
          )}
          {features.includes('competences') &&
            overviewItem(<EmojiEventsIcon sx={{ mr: '5px' }} />, 'competences.title', competencesCount, 'competences')}
          {overviewItem(<LanguageIcon sx={{ mr: '5px' }} />, 'language.title', languageChips)}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default ProfileOverview
